import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Per-ledger asset class registry.
 *
 * Wraps GET /asset-classes with a small in-memory cache + an explicit
 * `refresh()` for components that just mutated the list. Re-fetches
 * whenever the active ledger changes.
 */
const AssetClassesContext = createContext({
  classes: [],
  loading: false,
  refresh: () => {},
  labelFor: (k) => k,
  iconFor: (k) => null,
  colorFor: (k) => "#5C5F58",
  classByKey: (k) => null,
});

export function AssetClassesProvider({ children }) {
  const { user, activeLedgerId } = useAuth();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await api.get("/asset-classes");
      setClasses(r.data || []);
    } catch {
      setClasses([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh, activeLedgerId]);

  const classByKey = useCallback(
    (k) => classes.find((c) => c.key === k) || null,
    [classes],
  );
  const labelFor = useCallback(
    (k) => classByKey(k)?.label || (k ? k.replace(/_/g, " ") : "Other"),
    [classByKey],
  );
  const iconFor = useCallback((k) => classByKey(k)?.icon || null, [classByKey]);
  const colorFor = useCallback(
    (k) => classByKey(k)?.color || "#5C5F58",
    [classByKey],
  );

  return (
    <AssetClassesContext.Provider value={{ classes, loading, refresh, labelFor, iconFor, colorFor, classByKey }}>
      {children}
    </AssetClassesContext.Provider>
  );
}

export function useAssetClasses() {
  return useContext(AssetClassesContext);
}
